import { useLogger } from '~/composables/useLogger';

export interface PropertyOpenHouse {
	id: string;
	/** Date and time already expressed in Brasília time. */
	startAt: string;
	/** Date and time already expressed in Brasília time. */
	endAt: string;
}

const BRASILIA_OFFSET = '-03:00';
// The API uses a fixed -03:00 offset, including past visits. Avoid applying
// historical daylight-saving rules to an already-local Brasília timestamp.
const BRASILIA_TIME_ZONE = 'Etc/GMT+3';

export function parseOpenHouseDate(value: unknown): Date | null {
	if (typeof value !== 'string') return null;

	const normalized = value.trim().replace(' ', 'T');
	if (
		!/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])T([01]\d|2[0-3]):[0-5]\d:[0-5]\d$/.test(
			normalized
		)
	) {
		return null;
	}

	// Validate the wall-clock calendar without allowing Date to roll February 30
	// into March. The actual instant is parsed with Brasília's API offset below.
	const calendarDate = new Date(`${normalized}Z`);
	if (
		Number.isNaN(calendarDate.getTime()) ||
		calendarDate.toISOString().slice(0, 19) !== normalized
	) {
		return null;
	}

	return new Date(`${normalized}${BRASILIA_OFFSET}`);
}

export function getActiveOpenHouse(
	openHouses: PropertyOpenHouse[] | undefined,
	now: number
): PropertyOpenHouse | null {
	const active = (openHouses ?? [])
		.map(openHouse => ({
			openHouse,
			startAt: parseOpenHouseDate(openHouse.startAt)?.getTime() ?? null,
			endAt: parseOpenHouseDate(openHouse.endAt)?.getTime() ?? null
		}))
		.filter(
			(visit): visit is typeof visit & { startAt: number; endAt: number } =>
				visit.startAt !== null && visit.endAt !== null && visit.endAt > now
		)
		.sort((a, b) => a.startAt - b.startAt);

	return active[0]?.openHouse ?? null;
}

const dateFormatter = new Intl.DateTimeFormat('pt-BR', {
	timeZone: BRASILIA_TIME_ZONE,
	day: 'numeric',
	month: 'short'
});

const weekdayFormatter = new Intl.DateTimeFormat('pt-BR', {
	timeZone: BRASILIA_TIME_ZONE,
	weekday: 'short'
});

const timeFormatter = new Intl.DateTimeFormat('pt-BR', {
	timeZone: BRASILIA_TIME_ZONE,
	hour: 'numeric',
	minute: '2-digit',
	hourCycle: 'h23'
});

function getDateParts(date: Date): { day: string; month: string } {
	const parts = dateFormatter.formatToParts(date);
	const day = parts.find(part => part.type === 'day')?.value ?? '';
	const month = (parts.find(part => part.type === 'month')?.value ?? '')
		.replace('.', '')
		.trim();

	return { day, month };
}

export function formatOpenHouseDate(
	openHouse: PropertyOpenHouse
): string | null {
	const startAt = parseOpenHouseDate(openHouse.startAt);
	if (!startAt) return null;

	return formatScheduleDate(startAt, true);
}

function formatScheduleDate(date: Date, compact: boolean): string {
	const { day, month } = getDateParts(date);
	if (compact) {
		return `${day} ${month.charAt(0).toUpperCase()}${month.slice(1)}`;
	}
	const weekday = weekdayFormatter.format(date).replace('.', '');
	return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)}, ${day} ${month}`;
}

function formatScheduleTime(date: Date): string {
	const parts = timeFormatter.formatToParts(date);
	const hour = Number(parts.find(part => part.type === 'hour')?.value);
	const minutes = parts.find(part => part.type === 'minute')?.value;
	return `${hour}h${minutes === '00' ? '' : minutes}`;
}

export function formatOpenHouseSchedule(
	{ id, startAt, endAt }: PropertyOpenHouse,
	{ compact = false }: { compact?: boolean } = {}
): string {
	const startDate = parseOpenHouseDate(startAt);
	const endDate = parseOpenHouseDate(endAt);

	if (!startDate || !endDate) {
		useLogger('formatOpenHouseSchedule').warn(
			'Open House has an invalid schedule',
			{
				open_house_id: id,
				invalid_fields: [
					...(!startDate ? ['startAt'] : []),
					...(!endDate ? ['endAt'] : [])
				]
			}
		);
		return 'Data e horário a confirmar';
	}

	const start = `${formatScheduleDate(startDate, compact)} · ${formatScheduleTime(startDate)}`;
	const end = formatScheduleTime(endDate);
	const sameDay = startAt.trim().slice(0, 10) === endAt.trim().slice(0, 10);

	return sameDay
		? `${start}–${end}`
		: `${start} – ${formatScheduleDate(endDate, compact)} · ${end}`;
}
